import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { RefreshCw, FileText, ArrowLeft } from "lucide-react";
import {
  knowledgeGetKnowledgeChunk,
  knowledgeGetKnowledgeItemChunks,
} from "../gen";
import type {
  KnowledgeChunkDetailResponse,
  KnowledgeChunkResponse,
} from "../gen/models";
import { JsonViewer } from "../components/JsonViewer";
import { ChunkHeader } from "../components/chunk/ChunkHeader";
import { ChunkSidebarPanels } from "../components/chunk/ChunkSidebarPanels";

export function ChunkDetail() {
  const { chunk_id } = useParams<{ chunk_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [chunk, setChunk] = useState<KnowledgeChunkDetailResponse | null>(null);
  const [siblingChunks, setSiblingChunks] = useState<KnowledgeChunkResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedText, setCopiedText] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadChunkData = async () => {
      if (!chunk_id) return;
      setLoading(true);
      setError(null);

      try {
        const res = await knowledgeGetKnowledgeChunk(chunk_id);
        const data = (res as any).data as KnowledgeChunkDetailResponse;

        if (isCancelled) return;
        setChunk(data);

        // Fetch sibling chunks from parent item if available
        if (data.item_id) {
          try {
            const siblingsRes = await knowledgeGetKnowledgeItemChunks(data.item_id);
            if (!isCancelled) {
              setSiblingChunks(((siblingsRes as any).data || []) as KnowledgeChunkResponse[]);
            }
          } catch (sibErr) {
            console.warn("Could not load sibling chunks:", sibErr);
          }
        }
      } catch (err: any) {
        if (!isCancelled) {
          setError(err?.message || "Failed to load knowledge chunk");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadChunkData();

    return () => {
      isCancelled = true;
    };
  }, [chunk_id]);

  const handleCopyText = async () => {
    if (!chunk?.chunk_text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(chunk.chunk_text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = chunk.chunk_text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedText(true);
      setTimeout(() => setCopiedText(false), 2000);
    } catch (e) {
      console.error("Failed to copy text:", e);
    }
  };

  const handleCopyId = async () => {
    if (!chunk?.id) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(chunk.id);
      }
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } catch (e) {
      console.error("Failed to copy ID:", e);
    }
  };

  // Find index of current chunk among siblings
  const currentSiblingIdx = siblingChunks.findIndex((c) => c.id === chunk?.id);
  const prevChunk = currentSiblingIdx > 0 ? siblingChunks[currentSiblingIdx - 1] : null;
  const nextChunk =
    currentSiblingIdx >= 0 && currentSiblingIdx < siblingChunks.length - 1
      ? siblingChunks[currentSiblingIdx + 1]
      : null;

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-12 text-center text-stone-400 dark:text-stone-500 text-xs flex items-center justify-center gap-2">
        <RefreshCw className="w-4 h-4 animate-spin text-stone-500" />
        <span>Loading chunk content & vector embeddings...</span>
      </div>
    );
  }

  if (error || !chunk) {
    return (
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="p-8 text-center bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 text-rose-600 dark:text-rose-400 text-xs">
          {error || "Knowledge chunk not found"}
        </div>
      </div>
    );
  }

  const wordCount = chunk.chunk_text ? chunk.chunk_text.trim().split(/\s+/).filter(Boolean).length : 0;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="md:hidden pb-1">
        <button
          type="button"
          onClick={() => navigate(`/knowledge${location.search}`)}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Back to Knowledge Base</span>
        </button>
      </div>
      <ChunkHeader
        chunk={chunk}
        prevChunk={prevChunk}
        nextChunk={nextChunk}
        copiedId={copiedId}
        copiedText={copiedText}
        onNavigateChunk={(id) => navigate(`/knowledge/chunk/${id}${location.search}`)}
        onCopyId={handleCopyId}
        onCopyText={handleCopyText}
      />

      {/* 2-Column Grid: Left (Chunk Text Display), Right (Embedding Details & Sibling Navigation) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Left 2 Cols: Main Chunk Text Display */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs space-y-4">
            <div className="border-b border-stone-100 dark:border-stone-800 pb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                <h2 className="text-xs font-bold uppercase tracking-wider text-stone-800 dark:text-stone-200">
                  Chunk Text Content
                </h2>
              </div>
              <div className="flex items-center gap-3 text-[11px] font-mono text-stone-400 dark:text-stone-500">
                <span>{chunk.char_length || chunk.chunk_text.length} characters</span>
                <span>•</span>
                <span>{wordCount} words</span>
              </div>
            </div>

            {/* Chunk Text Container */}
            <div className="p-4 rounded-xl bg-stone-50 dark:bg-stone-950/70 border border-stone-200/80 dark:border-stone-800/80 text-stone-800 dark:text-stone-200 text-xs leading-relaxed whitespace-pre-wrap font-sans selection:bg-cyan-500/20 selection:text-cyan-200">
              {chunk.chunk_text}
            </div>

            {/* Payload Metadata if present */}
            {chunk.metadata && Object.keys(chunk.metadata).length > 0 && (
              <div className="pt-2">
                <JsonViewer
                  title="Chunk Metadata"
                  data={chunk.metadata}
                  maxHeight="max-h-[220px]"
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Col: Vector Info & Sibling Chunks */}
        <ChunkSidebarPanels chunk={chunk} siblingChunks={siblingChunks} />
      </div>
    </div>
  );
}
